import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api, Deposito, fmtMonto, haceCuanto } from "../api/client";
import { CalendarioRango, fechaCorta } from "../components/CalendarioRango";
import { Desplegable, OpcionMenu } from "../components/Desplegable";
import { Icon } from "../components/Icon";

/** Columnas de la tabla. `min-width` en la tarjeta hace que scrollee en vez de
 *  encimarse cuando la pantalla es angosta. */
const COLS = "74px minmax(0,1.5fr) 120px minmax(0,1fr) 92px 76px 74px";
const COLS_SEL = "26px " + COLS;

/** Cuántas filas se pintan de entrada. El resto entra al hacer scroll. */
const LOTE = 40;

/**
 * Solo el amarillo pide criterio.
 *
 * Verde y rojo significan que la detección fue concluyente: hay que
 * confirmarlos con una tecla, pero no hay nada que investigar. El amarillo es
 * el que dice «no me alcanza para afirmarlo», y es donde el analista aporta.
 */
const requiereRevision = (d: Deposito) => d.veredicto === "yellow";

/** Estados del certificador, en palabras del analista. */
const ESTADO_ARCA: Record<string, string> = {
  CONFIRMADO: "Confirmado",
  PARCIAL: "Parcial",
  ESPERANDO_DATA: "Sin cargar",
  SIN_MOVIMIENTO_CON_COBERTURA: "Sin abono",
  BANCO_FUERA_DE_ARCA: "Fuera de ARCA",
  SIN_CODIGO: "Sin código",
  SIN_FECHA: "Sin fecha",
  CONTRADICE: "Contradice",
  EXTORNADO: "Extornado",
  YA_CONSUMIDO: "Ya usado",
};

type Segmento = "criterio" | "no-coinciden" | "listos" | "sin-arca";

/** Los filtros que una vista guardada restaura. */
interface Filtros {
  segmento: Segmento | null;
  origen: string;
  espera: string;
  arcaF: string;
  montoMin: number;
}

const SIN_FILTROS: Filtros = {
  segmento: null, origen: "", espera: "", arcaF: "", montoMin: 0,
};

interface Vista { id: string; nombre: string; fija?: boolean; f: Filtros }

/** Las tres que vienen puestas. El resto las guarda cada quien con el `+`. */
const VISTAS_FIJAS: Vista[] = [
  { id: "turno", nombre: "Mi turno", fija: true,
    f: { ...SIN_FILTROS, segmento: "criterio" } },
  { id: "montos", nombre: "Montos > S/ 10 k", f: { ...SIN_FILTROS, montoMin: 10000 } },
  { id: "espera3", nombre: "Esperando +3 d", f: { ...SIN_FILTROS, espera: "+3" } },
];

const CLAVE_VISTAS = "vouchers.vistas";

/** Las vistas propias viven en el navegador de quien las creó. No hay dónde
 *  guardarlas del lado del servidor y no vale la pena inventarlo: una vista es
 *  una preferencia de trabajo, no un dato del negocio. */
function vistasGuardadas(): Vista[] {
  try {
    const crudo = localStorage.getItem(CLAVE_VISTAS);
    return crudo ? (JSON.parse(crudo) as Vista[]) : [];
  } catch {
    return [];
  }
}

/** Los períodos, del más chico al más grande. El orden importa: un conteo
 *  solo se puede calcular si la ventana cargada CONTIENE a ese período. */
const PERIODOS: { valor: string; label: string }[] = [
  { valor: "hoy", label: "Hoy" },
  { valor: "semana", label: "Esta semana" },
  { valor: "mes", label: "Este mes" },
  { valor: "todo", label: "Todo" },
];

/** Epoch del comienzo de un período, en hora local. */
function desdeDe(periodo: string) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  if (periodo === "hoy") return d.getTime() / 1000;
  if (periodo === "semana") {
    const l = new Date(d);
    l.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return l.getTime() / 1000;
  }
  if (periodo === "mes") return new Date(d.getFullYear(), d.getMonth(), 1).getTime() / 1000;
  return 0;
}

/** `Date` → `YYYY-MM-DD` en hora local. `toISOString` no sirve acá: convierte
 *  a UTC y en Lima adelanta el día cinco horas antes de que termine. */
function iso(d: Date) {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * Los cuatro grupos por los que se tría la cola.
 *
 * Reemplazan a los cuatro KPI pasivos que había antes: cada uno es un filtro,
 * no un número para mirar. La pregunta que responde la pantalla dejó de ser
 * «cuántos hay» y pasó a ser «qué miro primero».
 */
const SEGMENTOS: {
  id: Segmento; titulo: string; color: string; pie: string;
  test: (d: Deposito, esperaArca: boolean) => boolean;
}[] = [
  {
    id: "criterio", titulo: "Necesitan tu criterio", color: "var(--warn)",
    pie: "el agente no pudo confirmarlo",
    test: (d, esperaArca) => !esperaArca && requiereRevision(d),
  },
  {
    id: "no-coinciden", titulo: "No coinciden", color: "var(--danger)",
    pie: "listos para rechazar",
    test: (d, esperaArca) => !esperaArca && d.veredicto === "red",
  },
  {
    id: "listos", titulo: "Listos para confirmar", color: "var(--accent)",
    pie: "todo cuadra",
    test: (d, esperaArca) => !esperaArca && d.veredicto === "green",
  },
  {
    id: "sin-arca", titulo: "Sin abono en ARCA", color: "var(--muted)",
    pie: "el banco aún no lo confirma",
    test: (_d, esperaArca) => esperaArca,
  },
];

/** Lo que le falta a este depósito para decidirse solo. */
function queFalta(d: Deposito): string {
  if (d.motivos?.length) return d.motivos[0];
  if (d.veredicto === "green") return "Todo cuadra";
  if (d.veredicto === "red") return "No coincide con lo esperado";
  return "Revisar el voucher";
}

export default function PendingPage({
  items, cargando, falla, exigirArca = true, onRefrescar,
  rango = "semana", dia = "", aMedida = null, onRango,
}: {
  items: Deposito[];
  cargando: boolean;
  /** Por qué no se pudo cargar la cola. null = se cargó bien. */
  falla?: string | null;
  /** Solo se valida lo que ARCA confirmó; el resto espera aparte. */
  exigirArca?: boolean;
  onRefrescar: () => void;
  /** Ventana de tiempo: semana (default), mes, todo. */
  rango?: string;
  /** Un día puntual, YYYY-MM-DD. Tiene prioridad sobre `rango`. */
  dia?: string;
  /** Rango a medida elegido en el calendario: [desde, hasta]. */
  aMedida?: [string, string] | null;
  onRango?: (rango: string, dia?: string, aMedida?: [string, string] | null) => void;
}) {
  const navigate = useNavigate();
  const [segmento, setSegmento] = useState<Segmento | null>(null);
  const [busqueda, setBusqueda] = useState("");
  const [origen, setOrigen] = useState("");
  const [espera, setEspera] = useState("");
  const [arcaF, setArcaF] = useState("");
  /** Solo lo ponen las vistas guardadas; no tiene desplegable propio. */
  const [montoMin, setMontoMin] = useState(0);
  const [vista, setVista] = useState("");
  const [sugAbierto, setSugAbierto] = useState(false);
  /** Fila enfocada por teclado. -1 = ninguna. */
  const [cursor, setCursor] = useState(-1);
  const [tope, setTope] = useState(LOTE);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [modal, setModal] = useState(false);
  const [confirmado, setConfirmado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<string | null>(null);
  // La aprobación en lote nace apagada: se enciende desde Configuración.
  const [loteActivo, setLoteActivo] = useState(false);

  useEffect(() => {
    api.getConfig()
      .then((c: any) => setLoteActivo(c?.APROBACION_LOTE === true))
      .catch(() => setLoteActivo(false));
  }, []);
  const centinela = useRef<HTMLDivElement | null>(null);
  const buscador = useRef<HTMLInputElement | null>(null);

  // Un depósito sin abono confirmado por ARCA no se valida todavía: el
  // voucher puede ser impecable y la plata no haber entrado.
  //
  // `d.arca == null` = el backend todavía no manda el campo. Ahí NO se
  // filtra: si no sabemos el estado, esconder la cola sería inventar un
  // motivo para no mostrar trabajo real.
  const esperaArca = (d: Deposito) =>
    exigirArca && d.arca != null && !d.arca.confirmado;

  const conteos = useMemo(() => {
    const c: Record<Segmento, number> = {
      "criterio": 0, "no-coinciden": 0, "listos": 0, "sin-arca": 0,
    };
    for (const d of items) {
      const esp = esperaArca(d);
      for (const s of SEGMENTOS) if (s.test(d, esp)) c[s.id]++;
    }
    return c;
  }, [items, exigirArca]);

  /** Días que un depósito lleva esperando. */
  const diasEspera = (d: Deposito) =>
    d.creado_en ? (Date.now() / 1000 - d.creado_en) / 86400 : 0;

  const pasaEspera = (d: Deposito) => {
    const dd = diasEspera(d);
    return !espera || (espera === "hoy" && dd < 1)
      || (espera === "1-3" && dd >= 1 && dd <= 3)
      || (espera === "+3" && dd > 3);
  };

  const pasaArca = (d: Deposito) =>
    !arcaF || (arcaF === "si" && d.arca?.confirmado === true)
    || (arcaF === "no" && d.arca != null && !d.arca.confirmado)
    || (arcaF === "sin" && d.arca == null);

  const texto = busqueda.trim().toLowerCase();
  const coincideTexto = (d: Deposito) =>
    !texto || [d.titular, d.operacion, d.origen]
      .join(" ").toLowerCase().includes(texto);

  /**
   * Un depósito pasa los filtros.
   *
   * `omitir` deja uno afuera. Ese es todo el truco del **conteo facetado**:
   * cada opción se cuenta con los OTROS filtros aplicados, nunca con el
   * suyo. Contándola con el propio, elegir una opción dejaría a las demás en
   * cero y la lista de opciones dejaría de decir nada.
   */
  function pasa(d: Deposito, omitir?: "origen" | "espera" | "arca") {
    const esp = esperaArca(d);
    if (segmento) {
      const s = SEGMENTOS.find((x) => x.id === segmento)!;
      if (!s.test(d, esp)) return false;
    } else if (esp) {
      // Sin segmento elegido, la cola muestra lo decidible. Lo que espera a
      // ARCA tiene su propia tarjeta y no estorba acá.
      return false;
    }
    if (!coincideTexto(d)) return false;
    if (montoMin && Number(d.monto || 0) < montoMin) return false;
    if (omitir !== "origen" && origen && d.origen !== origen) return false;
    if (omitir !== "espera" && !pasaEspera(d)) return false;
    if (omitir !== "arca" && !pasaArca(d)) return false;
    return true;
  }

  const deps = [items, segmento, origen, espera, arcaF, montoMin, busqueda,
                exigirArca];

  const opcOrigen = useMemo<OpcionMenu[]>(() => {
    const base = items.filter((d) => pasa(d, "origen"));
    const cuenta = new Map<string, number>();
    for (const d of base) {
      if (d.origen) cuenta.set(d.origen, (cuenta.get(d.origen) ?? 0) + 1);
    }
    return [
      { valor: "", label: "Todos", n: base.length },
      ...[...cuenta.entries()].sort((a, b) => a[0].localeCompare(b[0]))
        .map(([o, n]) => ({ valor: o, label: o, n })),
    ];
  }, deps);

  const opcEspera = useMemo<OpcionMenu[]>(() => {
    const base = items.filter((d) => pasa(d, "espera"));
    const n = (f: (d: Deposito) => boolean) => base.filter(f).length;
    return [
      { valor: "", label: "Cualquiera", n: base.length },
      { valor: "hoy", label: "Menos de 1 día", n: n((d) => diasEspera(d) < 1) },
      { valor: "1-3", label: "1 a 3 días",
        n: n((d) => diasEspera(d) >= 1 && diasEspera(d) <= 3) },
      { valor: "+3", label: "Más de 3 días", n: n((d) => diasEspera(d) > 3) },
    ];
  }, deps);

  const opcArca = useMemo<OpcionMenu[]>(() => {
    const base = items.filter((d) => pasa(d, "arca"));
    const n = (f: (d: Deposito) => boolean) => base.filter(f).length;
    return [
      { valor: "", label: "Cualquiera", n: base.length },
      { valor: "si", label: "Confirmado", n: n((d) => d.arca?.confirmado === true) },
      { valor: "no", label: "Sin confirmar",
        n: n((d) => d.arca != null && !d.arca.confirmado) },
      { valor: "sin", label: "Sin dato", n: n((d) => d.arca == null) },
    ];
  }, deps);

  const visibles = useMemo(() => items.filter((d) => pasa(d)), deps);

  /**
   * Opciones del período, con conteo **solo donde se puede calcular**.
   *
   * `items` trae la ventana que se pidió al backend. Contar "Este mes"
   * teniendo cargada la semana obligaría a inventar un número; por eso el
   * conteo aparece únicamente en los períodos contenidos en el cargado, y en
   * el resto no hay número en vez de haber uno falso.
   */
  const opcPeriodo = useMemo<OpcionMenu[]>(() => {
    const cargado = dia || aMedida ? -1
      : PERIODOS.findIndex((p) => p.valor === rango);
    return PERIODOS.map((p, i) => ({
      valor: p.valor,
      label: p.label,
      n: cargado >= 0 && i <= cargado
        ? items.filter((d) => (d.creado_en ?? 0) >= desdeDe(p.valor)).length
        : undefined,
    }));
  }, [items, rango, dia, aMedida]);

  const etiquetaPeriodo = dia ? fechaCorta(dia)
    : aMedida ? `${fechaCorta(aMedida[0])} – ${fechaCorta(aMedida[1])}`
    : PERIODOS.find((p) => p.valor === rango)?.label ?? "Esta semana";

  const [vistasPropias, setVistasPropias] = useState<Vista[]>(vistasGuardadas);
  const vistas = [...VISTAS_FIJAS, ...vistasPropias];

  const filtrosAhora = (): Filtros => ({ segmento, origen, espera, arcaF, montoMin });

  function aplicarFiltros(f: Filtros) {
    setSegmento(f.segmento); setOrigen(f.origen); setEspera(f.espera);
    setArcaF(f.arcaF); setMontoMin(f.montoMin);
  }

  function alternarVista(v: Vista) {
    if (vista === v.id) { setVista(""); aplicarFiltros(SIN_FILTROS); return; }
    setVista(v.id); aplicarFiltros(v.f);
  }

  function guardarVista() {
    const nombre = window.prompt("Nombre de la vista")?.trim();
    if (!nombre) return;
    const nueva: Vista = { id: `p-${nombre}`, nombre, f: filtrosAhora() };
    const propias = [...vistasPropias.filter((v) => v.id !== nueva.id), nueva];
    setVistasPropias(propias);
    setVista(nueva.id);
    try {
      localStorage.setItem(CLAVE_VISTAS, JSON.stringify(propias));
    } catch {
      /* modo privado o almacenamiento lleno: la vista vale para esta sesión */
    }
  }

  function limpiarTodo() {
    aplicarFiltros(SIN_FILTROS);
    setBusqueda(""); setVista(""); onRango?.("semana", "", null);
  }

  /** Hasta 6 sugerencias, sobre titular y operación. */
  const sugerencias = useMemo(() => {
    const t = busqueda.trim().toLowerCase();
    if (t.length < 2) return [];
    return items
      .filter((d) => [d.titular, d.operacion].join(" ").toLowerCase().includes(t))
      .slice(0, 6);
  }, [items, busqueda]);

  // Los del segmento activo van juntos; sin segmento, primero lo que pide
  // criterio y dentro de cada grupo lo que lleva más tiempo esperando.
  const ordenados = useMemo(() => {
    const peso = (d: Deposito) =>
      requiereRevision(d) ? 0 : d.veredicto === "red" ? 1 : 2;
    return [...visibles].sort(
      (a, b) => peso(a) - peso(b) || (a.creado_en ?? 0) - (b.creado_en ?? 0));
  }, [visibles]);

  // Solo se pueden aprobar en lote los que el agente dio por concluyentes en
  // VERDE. Amarillo pide criterio y rojo pide rechazo: ninguno de los dos se
  // despacha sin abrirlo.
  const loteables = ordenados.filter(
    (d) => d.veredicto === "green" && !esperaArca(d));
  const seleccionados = ordenados.filter((d) => sel.has(d.id));
  const montoSel = seleccionados.reduce((s, d) => s + Number(d.monto || 0), 0);
  const selSinArca = seleccionados.filter((d) => !d.arca?.confirmado).length;

  function alternar(id: string) {
    setSel((prev) => {
      const s2 = new Set(prev);
      s2.has(id) ? s2.delete(id) : s2.add(id);
      return s2;
    });
  }

  function seleccionarGrupo(ids: string[]) {
    setSel((prev) => {
      const s2 = new Set(prev);
      const todos = ids.every((i) => s2.has(i));
      ids.forEach((i) => (todos ? s2.delete(i) : s2.add(i)));
      return s2;
    });
  }

  async function aprobarLote() {
    if (!confirmado || enviando) return;
    setEnviando(true);
    try {
      const r = await api.aprobarLote([...sel]);
      setResultado(
        r.fallidos.length
          ? `${r.aprobados.length} aprobados · ${r.fallidos.length} no se pudieron`
          : `${r.aprobados.length} aprobados`);
      setSel(new Set()); setModal(false); setConfirmado(false);
      onRefrescar();
    } catch (e: any) {
      setResultado(e?.message || "No se pudo aprobar el lote.");
    } finally {
      setEnviando(false);
    }
  }

  // Un número, una verdad: el encabezado describe el PERÍODO —lo mismo que
  // cuenta el badge del menú— y el pie de la tabla describe lo que quedó
  // después de filtrar. Antes los dos decían «depósitos» sobre conjuntos
  // distintos y se contradecían apenas se tocaba un filtro.
  const montoEnEspera = items.reduce((s, d) => s + Number(d.monto || 0), 0);
  const masAntiguo = items.reduce<Deposito | null>(
    (a, d) => (!a || (d.creado_en ?? 0) < (a.creado_en ?? 0) ? d : a), null);

  const hayFiltro = !!(segmento || origen || espera || arcaF || montoMin
                       || busqueda || dia || aMedida || rango !== "semana");

  useEffect(() => {
    setTope(LOTE); setCursor(-1);
  }, [segmento, origen, espera, arcaF, montoMin, busqueda, items.length]);

  useEffect(() => {
    const el = centinela.current;
    if (!el) return;
    const obs = new IntersectionObserver((e) => {
      if (e[0]?.isIntersecting) setTope((t) => t + LOTE);
    }, { rootMargin: "400px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [ordenados.length]);

  // Atajos de bandeja: `/` busca, `j`/`k` recorren, `x` selecciona, `↵` abre.
  // Son los de cualquier cliente de correo — quien despacha una cola todos los
  // días no debería tener que ir al mouse para pasar a la siguiente fila.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const dentroDeCampo = ["INPUT", "TEXTAREA", "SELECT"].includes(
        (e.target as HTMLElement)?.tagName);
      if (dentroDeCampo || e.metaKey || e.ctrlKey || e.altKey) return;
      const alcance = Math.min(ordenados.length, tope);
      if (e.key === "/") {
        e.preventDefault(); buscador.current?.focus();
      } else if (e.key === "j" && alcance) {
        e.preventDefault(); setCursor((c) => Math.min(alcance - 1, c + 1));
      } else if (e.key === "k" && alcance) {
        e.preventDefault(); setCursor((c) => Math.max(0, c - 1));
      } else if (e.key === "x" && cursor >= 0 && loteActivo) {
        const d = ordenados[cursor];
        // `x` no puede seleccionar lo que el lote no acepta: sería una
        // selección invisible que el modal después descarta sin explicar.
        if (d && d.veredicto === "green" && !esperaArca(d)) {
          e.preventDefault(); alternar(d.id);
        }
      } else if (e.key === "Enter" && cursor >= 0) {
        const d = ordenados[cursor];
        if (d) navigate(`/pendientes/${d.id}`);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ordenados, tope, cursor, loteActivo]);

  // La fila enfocada tiene que verse; si no, `j` parece no hacer nada.
  useEffect(() => {
    if (cursor < 0) return;
    document.querySelector(`[data-fila="${cursor}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [cursor]);

  if (cargando) return <div className="empty">Cargando la cola...</div>;

  // Si no se pudo cargar, no se muestran ni el encabezado ni la tabla: un
  // "0 por validar" al lado de un error es la misma mentira en letra chica.
  if (falla)
    return (
      <div className="empty falla">
        <div className="falla-titulo">No se pudo cargar la cola de depósitos.</div>
        <div className="falla-detalle mono">{falla}</div>
        <div className="falla-nota">
          Puede haber depósitos esperando decisión. Esta pantalla no los está
          mostrando.
        </div>
        <button className="btn" onClick={onRefrescar}>Reintentar</button>
      </div>
    );

  const titulo = dia ? `Lo que llegó el ${fechaCorta(dia)}`
    : aMedida ? "Lo que llegó en el rango elegido"
    : rango === "hoy" ? "Lo que llegó hoy"
    : rango === "mes" ? "Lo que llegó este mes"
    : rango === "todo" ? "Todo lo que está sin decidir"
    : "Lo que llegó esta semana";

  /**
   * El período, en fechas de verdad.
   *
   * «Esta semana» no dice *cuál* semana: quien abre el portal el lunes y quien
   * lo abre el domingo leen el mismo texto sobre ventanas distintas. El rango
   * explícito es lo que vuelve verificable al resumen de la derecha — sin él,
   * «51 depósitos en espera» es un número sin período al que referirse.
   */
  const rangoFechas = (() => {
    if (dia) return fechaCorta(dia);
    if (aMedida) return `${fechaCorta(aMedida[0])} – ${fechaCorta(aMedida[1])}`;
    if (rango === "todo") return "sin límite de fecha";
    const desde = iso(new Date(desdeDe(rango) * 1000));
    const hasta = iso(new Date());
    return desde === hasta ? fechaCorta(hasta)
      : `${fechaCorta(desde)} – ${fechaCorta(hasta)}`;
  })();

  /** Por qué se está viendo ese rango. Solo cuando hay algo que aclarar. */
  const notaPeriodo = dia ? "día puntual"
    : aMedida ? "rango a medida"
    : rango === "semana" ? "por defecto"
    : null;

  const decidibles = conteos["criterio"] + conteos["no-coinciden"] + conteos["listos"];

  return (
    <>
      {/* ── Encabezado de triaje ─────────────────────────────────────────
          Reemplaza los cuatro KPI pasivos: cada tarjeta es un filtro. La
          pregunta pasó de «cuántos hay» a «qué miro primero». */}
      <div className="card card-pad triaje">
        <div className="triaje-top">
          <div className="triaje-titulo">{titulo}</div>
          <div className="triaje-periodo">
            {rangoFechas}{notaPeriodo && ` · ${notaPeriodo}`}
          </div>
          <div className="triaje-resumen tabular">
            {items.length} depósitos · {fmtMonto(montoEnEspera, "PEN")} en espera
            {masAntiguo?.creado_en && <> · el más antiguo lleva {haceCuanto(masAntiguo.creado_en)}</>}
          </div>
        </div>

        {decidibles > 0 && (
          <div className="triaje-barra" aria-hidden="true">
            {SEGMENTOS.filter((s) => s.id !== "sin-arca").map((s) => (
              <span key={s.id}
                    style={{ width: `${100 * conteos[s.id] / decidibles}%`,
                             background: s.color }} />
            ))}
          </div>
        )}

        <div className="triaje-grid">
          {SEGMENTOS.map((s) => {
            const activo = segmento === s.id;
            return (
              <button key={s.id}
                      className={"triaje-card" + (activo ? " on" : "")}
                      style={activo ? { borderColor: s.color } : undefined}
                      onClick={() => setSegmento(activo ? null : s.id)}>
                <span className="triaje-label" style={{ color: s.color }}>
                  <i style={{ background: s.color }} />{s.titulo}
                </span>
                <span className="triaje-num tabular">{conteos[s.id]}</span>
                <span className="triaje-pie">{s.pie}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Filtros ──────────────────────────────────────────────────── */}
      <div className="filters">
        <div className="buscador-caja">
          <div className="buscador">
            <Icon name="search" />
            <input ref={buscador} value={busqueda} placeholder="Titular o n° de operación"
                   onFocus={() => setSugAbierto(true)}
                   onBlur={() => setTimeout(() => setSugAbierto(false), 120)}
                   onChange={(e) => { setBusqueda(e.target.value); setSugAbierto(true); }} />
            {busqueda
              ? <button className="buscador-x" onClick={() => setBusqueda("")}
                        aria-label="Limpiar búsqueda">✕</button>
              : <kbd>/</kbd>}
          </div>

          {sugAbierto && sugerencias.length > 0 && (
            <div className="buscador-pop">
              {sugerencias.map((d) => (
                <button key={d.id} className="buscador-op"
                        onMouseDown={() => navigate(`/pendientes/${d.id}`)}>
                  <span className="mono org">{d.origen}</span>
                  <span className="tit">{d.titular}</span>
                  <span className="mono op">op {d.operacion ?? "—"}</span>
                </button>
              ))}
              <div className="buscador-nota">
                {visibles.length === 1
                  ? "1 coincidencia en la cola"
                  : `${visibles.length} coincidencias en la cola`}
              </div>
            </div>
          )}
        </div>

        <Desplegable etiqueta="Período" valor={etiquetaPeriodo} ancho={276}
                     opciones={opcPeriodo}
                     activo={!!dia || !!aMedida || rango !== "semana"}
                     onElegir={(v) => { onRango?.(v, "", null); setVista(""); }}
                     pie={<CalendarioRango
                            onAplicar={(a, b) => onRango?.(rango, "", [a, b])} />} />

        <Desplegable etiqueta="Origen" valor={origen || "Todos"} opciones={opcOrigen}
                     activo={!!origen} onElegir={setOrigen} />
        <Desplegable etiqueta="Espera" opciones={opcEspera} activo={!!espera}
                     valor={opcEspera.find((o) => o.valor === espera)?.label ?? "Cualquiera"}
                     onElegir={setEspera} />
        <Desplegable etiqueta="ARCA" opciones={opcArca} activo={!!arcaF}
                     valor={opcArca.find((o) => o.valor === arcaF)?.label ?? "Cualquiera"}
                     onElegir={setArcaF} />

        <button className={"filter-limpiar" + (hayFiltro ? " on" : "")}
                disabled={!hayFiltro} onClick={limpiarTodo}>
          Limpiar
        </button>

        <span style={{ flex: 1 }} />

        <div className="vistas">
          <span className="vistas-tit">Vistas</span>
          {vistas.map((v) => (
            <button key={v.id} className={"vista" + (vista === v.id ? " on" : "")}
                    onClick={() => alternarVista(v)}>
              {v.fija && (
                <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                  <path d="M8 1.8l1.9 3.9 4.3.6-3.1 3 .8 4.3L8 11.6 4.1 13.6l.8-4.3-3.1-3 4.3-.6z" />
                </svg>
              )}
              {v.nombre}
            </button>
          ))}
          <button className="vista-mas" disabled={!hayFiltro} onClick={guardarVista}
                  title={hayFiltro
                    ? "Guardar los filtros de ahora como una vista"
                    : "Poné algún filtro para poder guardarlo"}>
            +
          </button>
        </div>

        <button className="filter" onClick={() => navigate("/historico")}>Ya decididos</button>
        <button className="btn" onClick={onRefrescar}>Refrescar</button>
      </div>

      {/* ── Tabla ────────────────────────────────────────────────────── */}
      <div className="card tabla-cola">
        <div className="thead" style={{ gridTemplateColumns: loteActivo ? COLS_SEL : COLS }}>
          {loteActivo && (
            <div>
              <input type="checkbox" aria-label="Seleccionar todos los que cuadran"
                     checked={loteables.length > 0 && loteables.every((d) => sel.has(d.id))}
                     onChange={() => seleccionarGrupo(loteables.map((d) => d.id))} />
            </div>
          )}
          <div>Origen</div><div>Titular</div><div>Monto</div>
          <div>Qué falta</div><div>ARCA</div><div>Espera</div><div />
        </div>

        {ordenados.length === 0 && (
          <div className="empty">
            {items.length === 0
              ? "No hay depósitos esperando decisión."
              : segmento === "sin-arca"
                ? "Ningún depósito espera confirmación de ARCA."
                : hayFiltro
                  ? "Ningún depósito cumple estos filtros."
                  : `Ninguno necesita criterio ahora. Quedan ${items.length} en la cola.`}
            {hayFiltro && (
              <div style={{ marginTop: 10 }}>
                <button className="btn" onClick={() => {
                  setSegmento(null); setOrigen(""); setBusqueda(""); onRango?.("semana", "");
                }}>
                  Quitar filtros
                </button>
              </div>
            )}
          </div>
        )}

        {ordenados.slice(0, tope).map((d, i) => {
          const espera = esperaArca(d);
          // Cabecera cuando arranca un grupo nuevo. Solo tiene sentido sin
          // segmento elegido: dentro de un segmento son todos del mismo.
          const anterior = i > 0 ? ordenados[i - 1] : null;
          const grupo = !segmento && (!anterior || anterior.veredicto !== d.veredicto)
            ? SEGMENTOS.find((sg) => sg.test(d, espera)) : null;
          const delGrupo = grupo
            ? ordenados.filter((x) => grupo.test(x, esperaArca(x)))
            : [];
          const dias = d.creado_en ? (Date.now() / 1000 - d.creado_en) / 86400 : 0;
          const arca = d.arca?.estado ? (ESTADO_ARCA[d.arca.estado] ?? d.arca.estado) : "—";
          return (
            <div key={d.id}>
              {grupo && (
                <div className="grupo-cab">
                  <i style={{ background: grupo.color }} />
                  <span style={{ color: grupo.color }}>{grupo.titulo}</span>
                  <span className="grupo-nota">
                    {delGrupo.length} acá · {fmtMonto(
                      delGrupo.reduce((s2, x) => s2 + Number(x.monto || 0), 0), "PEN")}
                  </span>
                  {loteActivo && grupo.id === "listos" && (
                    <button className="grupo-accion"
                            onClick={() => seleccionarGrupo(delGrupo.map((x) => x.id))}>
                      {delGrupo.length === 1 ? "Seleccionar" : `Seleccionar los ${delGrupo.length}`}
                    </button>
                  )}
                </div>
              )}
            <div data-fila={i}
                 className={"trow fila-cola" + (sel.has(d.id) ? " sel" : "")
                            + (cursor === i ? " foco" : "")}
                 style={{ gridTemplateColumns: loteActivo ? COLS_SEL : COLS, cursor: "pointer" }}
                 onClick={() => navigate(`/pendientes/${d.id}`)}>
              {loteActivo && (
                <div onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={sel.has(d.id)}
                         disabled={d.veredicto !== "green" || espera}
                         title={d.veredicto !== "green"
                           ? "Solo se aprueban en lote los que cuadran del todo"
                           : undefined}
                         onChange={() => alternar(d.id)} />
                </div>
              )}
              <div className="mono col-origen">{d.origen}</div>

              <div style={{ minWidth: 0 }}>
                <div className="col-titular">{d.titular ?? "—"}</div>
                <div className="mono col-op">op {d.operacion ?? "—"}</div>
              </div>

              <div className="tabular col-monto">{fmtMonto(d.monto, d.moneda)}</div>

              <div className="col-falta"
                   style={{ color: requiereRevision(d) ? "var(--warn)" : "var(--muted)" }}>
                {queFalta(d)}
              </div>

              <div className="col-arca">
                <i style={{ background: d.arca?.confirmado ? "var(--accent)" : "var(--muted)" }} />
                {arca}
              </div>

              <div className="tabular col-espera"
                   style={{ color: dias >= 3 ? "var(--danger)" : "var(--muted)" }}>
                {haceCuanto(d.creado_en)}
              </div>

              <div style={{ textAlign: "right" }}>
                <button className={"btn" + (requiereRevision(d) && !espera ? " primary" : "")}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/pendientes/${d.id}`);
                        }}>
                  {espera ? "Ver" : "Revisar"}
                </button>
              </div>
            </div>
            </div>
          );
        })}

        {ordenados.length > tope && (
          <div ref={centinela} className="empty" style={{ padding: "18px 16px" }}>
            Mostrando {tope} de {ordenados.length}…
          </div>
        )}

        {ordenados.length > 0 && ordenados.length <= tope && (
          <div className="tabla-pie">
            Mostrando {ordenados.length}
            {ordenados.length !== items.length && <> de {items.length}</>}
            {" · "}orden: lo que pide criterio primero, después lo que lleva
            más tiempo esperando
            <span className="atajos">
              <kbd>j</kbd><kbd>k</kbd> recorrer
              {loteActivo && <> · <kbd>x</kbd> seleccionar</>}
              {" · "}<kbd>↵</kbd> abrir
            </span>
          </div>
        )}
      </div>

      {/* Barra flotante de selección */}
      {loteActivo && sel.size > 0 && (
        <div className="lote-barra">
          <span className="tabular">
            <b>{sel.size}</b> seleccionados · {fmtMonto(montoSel, "PEN")}
          </span>
          {selSinArca > 0 && (
            <span className="lote-aviso">
              {selSinArca} sin abono confirmado por ARCA
            </span>
          )}
          <button className="btn ghost" onClick={() => setSel(new Set())}>
            Deseleccionar
          </button>
          <button className="btn primary"
                  onClick={() => { setConfirmado(false); setModal(true); }}>
            Aprobar {sel.size}
          </button>
        </div>
      )}

      {/* Modal de confirmación. Es obligatorio y el botón queda deshabilitado
          hasta marcar el checkbox: aprobar en lote es confirmar sobre una
          lista sin haber abierto cada comprobante, y eso tiene que costar un
          gesto explícito. */}
      {modal && (
        <div className="overlay" onClick={() => setModal(false)}>
          <div className="lote-modal" onClick={(e) => e.stopPropagation()}>
            <div className="lote-modal-tit">Aprobar {sel.size} depósitos</div>
            <div className="lote-modal-sub">
              Se ejecuta en el Admin real. {fmtMonto(montoSel, "PEN")} en total.
              Esta acción no se puede deshacer desde el portal.
            </div>

            {selSinArca > 0 && (
              <div className="met-aviso met-aviso-warn">
                <b>{selSinArca} sin abono confirmado por ARCA.</b> El voucher
                cuadra, pero el banco todavía no confirma que la plata entró.
              </div>
            )}

            <div className="lote-lista">
              {seleccionados.map((d) => (
                <div key={d.id} className="lote-item">
                  <span className="mono col-origen">{d.origen}</span>
                  <span className="col-titular">{d.titular}</span>
                  <span className="tabular col-monto">{fmtMonto(d.monto, d.moneda)}</span>
                  <span className="col-arca">
                    <i style={{ background: d.arca?.confirmado ? "var(--accent)" : "var(--warn)" }} />
                    {d.arca?.confirmado ? "Confirmado" : "Sin confirmar"}
                  </span>
                </div>
              ))}
            </div>

            <label className="lote-check">
              <input type="checkbox" checked={confirmado}
                     onChange={(e) => setConfirmado(e.target.checked)} />
              Revisé los {sel.size} y confirmo la aprobación
            </label>

            <div className="rev-acciones">
              <button className="btn primary rev-aprobar"
                      disabled={!confirmado || enviando}
                      onClick={aprobarLote}>
                {enviando ? "Aprobando..." : `Aprobar ${sel.size}`}
              </button>
              <button className="btn ghost" disabled={enviando}
                      onClick={() => setModal(false)}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {resultado && <div className="toast" onClick={() => setResultado(null)}>{resultado}</div>}
    </>
  );
}
